import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  FormHelperText,
  Paper,
  Divider,
  Box,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import { toast } from 'react-toastify';
// Import API functions
import { createTransportLine, updateTransportLine } from '../../api';
// Iconos
import BusinessIcon from '@mui/icons-material/Business';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EmailIcon from '@mui/icons-material/Email';

const TransportLineForm = ({ open, onClose, onSubmit, initialData }) => {
  const theme = useTheme();  const [formData, setFormData] = useState({
    alta_SAP: initialData?.alta_SAP || '',
    razon_Social: initialData?.razon_Social || '',
    name: initialData?.name || '',
    telefono: initialData?.telefono || '',
    email: initialData?.email || '',
    additional_emails: initialData?.additional_emails || initialData?.additionalEmails || []
  });
  
  const [validationErrors, setValidationErrors] = useState({
    telefono: '',
    email: '',
    additional_emails: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("all"); // Para futuras expansiones con secciones

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone) return ''; // Phone is optional, so empty is fine
    
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone) ? '' : 'El teléfono debe tener exactamente 10 dígitos';
  };
  const validateEmail = (email) => {
    if (!email) return ''; // Email is optional, so empty is fine
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Por favor ingresa un correo electrónico válido';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({...formData, [name]: value});
    
    // Validate fields if they're phone or email
    if (name === 'telefono') {
      setValidationErrors({
        ...validationErrors, 
        telefono: validatePhone(value)
      });
    } 
    else if (name === 'email') {
      setValidationErrors({
        ...validationErrors, 
        email: validateEmail(value)
      });
    }
  };
  // Funciones para manejar correos electrónicos adicionales
  const handleAdditionalEmailChange = (index, value) => {
    const newAdditionalEmails = [...formData.additional_emails];
    newAdditionalEmails[index] = value;
    
    // Actualizar formData
    setFormData({
      ...formData,
      additional_emails: newAdditionalEmails
    });
    
    // Validar
    const newValidationErrors = [...validationErrors.additional_emails];
    newValidationErrors[index] = validateEmail(value);
    
    setValidationErrors({
      ...validationErrors,
      additional_emails: newValidationErrors
    });
  };
  
  const addAdditionalEmail = () => {
    setFormData({
      ...formData,
      additional_emails: [...formData.additional_emails, '']
    });
    
    setValidationErrors({
      ...validationErrors,
      additional_emails: [...validationErrors.additional_emails, '']
    });
  };
  
  const removeAdditionalEmail = (index) => {
    const newAdditionalEmails = [...formData.additional_emails];
    newAdditionalEmails.splice(index, 1);
    
    const newValidationErrors = [...validationErrors.additional_emails];
    newValidationErrors.splice(index, 1);
    
    setFormData({
      ...formData,
      additional_emails: newAdditionalEmails
    });
    
    setValidationErrors({
      ...validationErrors,
      additional_emails: newValidationErrors
    });
  };  const isFormValid = () => {
    // Check if there are any validation errors in main fields
    if (validationErrors.telefono || validationErrors.email) {
      return false;
    }
    
    // Check additional emails
    for (const error of validationErrors.additional_emails) {
      if (error) {
        return false;
      }
    }
    
    return true;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
      // Final validation before submitting
    const phoneError = validatePhone(formData.telefono);
    const emailError = validateEmail(formData.email);
    
    // Validar correos adicionales
    const additionalEmailErrors = formData.additional_emails.map(email => validateEmail(email));
    
    setValidationErrors({
      telefono: phoneError,
      email: emailError,
      additional_emails: additionalEmailErrors
    });
    
    // Don't submit if there are validation errors
    if (phoneError || emailError || additionalEmailErrors.some(error => error)) {
      return;
    }
    
    // Filtrar correos adicionales vacíos antes de enviar
    const cleanedFormData = {
      ...formData,
      additional_emails: formData.additional_emails.filter(email => email.trim() !== '')
    };
    
    setIsSubmitting(true);
    setError(null);
      try {
      let result;
      
      if (initialData) {
        // When editing, use updateTransportLine
        result = await updateTransportLine(initialData.id, cleanedFormData);
        toast.success('Línea de transporte actualizada correctamente');
      } else {
        // When creating, use createTransportLine
        result = await createTransportLine(cleanedFormData);
        toast.success('Línea de transporte creada correctamente. Pendiente de autorización.');
      }
      
      onSubmit(result);  // Pass the updated data to the parent component
      onClose();
    } catch (error) {
      console.error('Error submitting transport line:', error);
      
      // Extract error message from the error object
      let errorMessage = 'Error desconocido';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.detail || error.response.data.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: theme.palette.primary.main, 
        color: 'white',
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box display="flex" alignItems="center">
          <BusinessIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="span">
            {initialData ? 'Editar Línea de Transporte' : 'Nueva Línea de Transporte'}
          </Typography>
        </Box>
        <Tooltip title="Las líneas de transporte son proveedores de servicios de transporte con los que tu empresa trabaja">
          <IconButton size="small" sx={{ color: 'white' }}>
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {!initialData && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 1 }}>
              Las nuevas líneas de transporte requieren autorización del departamento legal antes de ser activadas.
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>
              {error}
            </Alert>
          )}
          
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 'bold',
              mb: 2,
              color: theme.palette.primary.main
            }}>
              <BusinessIcon sx={{ mr: 1 }} />
              Información de la Empresa
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ID SAP"
                    name="alta_SAP"
                    value={formData.alta_SAP}
                    onChange={handleInputChange}
                    required
                    disabled={!!initialData}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Razón Social"
                    name="razon_Social"
                    value={formData.razon_Social}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre Comercial"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
            <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 'bold',
              mb: 2,
              color: theme.palette.primary.main
            }}>
              <ContactPhoneIcon sx={{ mr: 1 }} />
              Información de Contacto
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    error={!!validationErrors.telefono}
                    helperText={validationErrors.telefono}
                    inputProps={{ 
                      maxLength: 10,
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    placeholder="10 dígitos"
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label="Correo electrónico principal"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!validationErrors.email}
                      helperText={validationErrors.email}
                      placeholder="ejemplo@correo.com"
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 1 }
                      }}
                    />
                    <Tooltip title="Añadir correo adicional">
                      <IconButton 
                        color="primary" 
                        onClick={addAdditionalEmail}
                        sx={{ ml: 1, mt: 1 }}
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>              
              {/* Correos electrónicos adicionales */}              {formData.additional_emails.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    color: theme.palette.text.secondary,
                    mb: 1
                  }}>
                    <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                    Correos electrónicos adicionales
                  </Typography>
                    {formData.additional_emails.map((email, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        mb: 2 
                      }}
                    >
                      <TextField
                        fullWidth
                        label={`Correo adicional ${index + 1}`}
                        type="email"
                        value={email}
                        onChange={(e) => handleAdditionalEmailChange(index, e.target.value)}
                        error={!!validationErrors.additional_emails[index]}
                        helperText={validationErrors.additional_emails[index]}
                        placeholder="ejemplo@correo.com"
                        variant="outlined"
                        size="small"
                        InputProps={{
                          sx: { borderRadius: 1 }
                        }}
                      />
                      <IconButton 
                        color="error" 
                        onClick={() => removeAdditionalEmail(index)}
                        sx={{ ml: 1 }}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                      {index === formData.additional_emails.length - 1 && (
                        <IconButton 
                          color="primary" 
                          onClick={addAdditionalEmail}
                          sx={{ ml: 1 }}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
          
          {/* Espacio para futuras secciones */}
          <Box sx={{ mt: 3, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              * Campos obligatorios
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          borderTop: `1px solid ${theme.palette.divider}`,
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={onClose}
            sx={{ 
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={isSubmitting || !isFormValid()}
            sx={{ 
              minWidth: 120,
              borderRadius: 1,
              boxShadow: 2,
              textTransform: 'none'
            }}
          >
            {isSubmitting ? 'Enviando...' : (initialData ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TransportLineForm;
